import { AccountProvider, AuthGuard } from "@qw/auth";
import {
  AuthActivateAccount,
  AuthForgotPassword,
  AuthSignIn,
  CenteredLayout,
  ContactsProvider,
  PaginationProvider,
  Toaster,
} from "@qw/ui";
import { createBrowserRouter } from "react-router";
import { AppLayout } from "./app/app-layout";
import { DefaultRouteRedirect } from "./app/default-route-redirect";
import { QwibLayout } from "./app/qwib-layout";
import { BankanColumn } from "./bankan/bankan-column";
import { BankanLayout } from "./bankan/bankan-layout";
import { BankanRedirect } from "./bankan/bankan-redirect";
import { CarrierDetail } from "./carriers/carrier-detail";
import { CarriersIndex } from "./carriers/carriers-index";
import { CarriersLayout } from "./carriers/carriers-layout";
import { ChatSessionView } from "./chat/chat-session-view";
import { ClassificationsLayout } from "./classifications/classifications-layout";
import { ClassificationsPage } from "./classifications/classifications-page";
import { Drafts } from "./drafts/drafts";
import { InboxLayout } from "./inbox/inbox-layout";
import { miniAppRoutes } from "./mini-app/mini-app-routes";
import { PlaybookEditor } from "./playbooks/playbook-editor/playbook-editor";
import { PlaybookRunDetail } from "./playbooks/playbook-run-detail";
import { PlaybookRunsList } from "./playbooks/playbook-runs-list";
import { PlaybookSetDetail } from "./playbooks/playbook-set-detail";
import { PlaybooksDiscover } from "./playbooks/playbooks-discover";
import { PlaybooksLayout } from "./playbooks/playbooks-layout";
import { PlaybooksIndex } from "./playbooks/playbooks-library";
import { PlaybooksTriggers } from "./playbooks/playbooks-triggers";
import { PostHogTracker } from "./posthog-tracker";
import { ProductEditor } from "./products/product-editor";
import { ProductsIndex } from "./products/products-index";
import { ProductsLayout } from "./products/products-layout";
import { BookProvider } from "./providers/book-provider";
import { ConversationProvider } from "./providers/conversation-provider";
import { DraftsComposeProvider } from "./providers/drafts-compose-provider";
import { DraftsFilterProvider } from "./providers/drafts-filter-provider";
import { InboxFilterProvider } from "./providers/inbox-filter-provider";
import { InboxQueryStatesProvider } from "./providers/inbox-query-states-provider";
import { InsuredProvider } from "./providers/insured-provider";
import { PageProvider } from "./providers/page-provider";

export const router = createBrowserRouter([
  {
    element: (
      <>
        <AuthGuard>
          <AccountProvider>
            <PostHogTracker />
            <ContactsProvider>
              <InboxQueryStatesProvider>
                <PageProvider rootPath="/inbox">
                  <DraftsComposeProvider>
                    <PaginationProvider rowsPerPageOptions={["25", "50"]} shouldResetPageOnNumRecordsChange={false}>
                      <InboxFilterProvider>
                        <ConversationProvider>
                          <InsuredProvider>
                            <AppLayout />
                          </InsuredProvider>
                        </ConversationProvider>
                      </InboxFilterProvider>
                    </PaginationProvider>
                  </DraftsComposeProvider>
                </PageProvider>
              </InboxQueryStatesProvider>
            </ContactsProvider>
          </AccountProvider>
        </AuthGuard>
        <Toaster />
      </>
    ),
    children: [
      {
        path: "bankan",
        element: <BankanLayout />,
        children: [
          {
            index: true,
            element: <BankanRedirect />,
          },
          {
            path: ":column",
            element: <BankanColumn />,
          },
        ],
      },
      {
        element: (
          <BookProvider>
            <QwibLayout />
          </BookProvider>
        ),
        children: [
          {
            path: "inbox",
            element: <InboxLayout />,
            children: miniAppRoutes(),
          },
          {
            path: "drafts/:insuredId?",
            element: (
              <DraftsFilterProvider>
                <Drafts />
              </DraftsFilterProvider>
            ),
          },
          {
            path: "chat/:chatSessionId",
            element: <ChatSessionView />,
          },
        ],
      },
      {
        path: "playbooks",
        element: <PlaybooksLayout />,
        children: [
          {
            index: true,
            element: <PlaybooksIndex />,
          },
          {
            path: "discover",
            element: <PlaybooksDiscover />,
          },
          {
            path: "discover/:setId",
            element: <PlaybookSetDetail />,
          },
          {
            path: "triggers",
            element: <PlaybooksTriggers />,
          },
          {
            path: "runs",
            element: <PlaybookRunsList />,
          },
          {
            path: "runs/:playbookRunId",
            element: <PlaybookRunDetail />,
          },
          {
            path: ":playbookId",
            element: <PlaybookEditor />,
          },
        ],
      },
      {
        path: "classifications",
        element: <ClassificationsLayout />,
        children: [
          {
            index: true,
            element: <ClassificationsPage />,
          },
        ],
      },
      {
        path: "carriers",
        element: <CarriersLayout />,
        children: [
          {
            index: true,
            element: <CarriersIndex />,
          },
          {
            path: ":id",
            element: <CarrierDetail />,
          },
        ],
      },
      {
        path: "products",
        element: <ProductsLayout />,
        children: [
          {
            index: true,
            element: <ProductsIndex />,
          },
          {
            path: ":productId",
            element: <ProductEditor />,
          },
        ],
      },
      {
        index: true,
        element: <DefaultRouteRedirect />,
      },
    ],
  },
  {
    element: <CenteredLayout />,
    children: [
      {
        path: "login",
        element: <AuthSignIn />,
      },
      {
        path: "forgot-password",
        element: <AuthForgotPassword />,
      },
      {
        path: "activate-account/:changePasswordId",
        element: <AuthActivateAccount />,
      },
    ],
  },
]);
